import { filterDuplicates, isNonEmptyString } from '@mbrtargeting/metatag-utils';
import { effect, signal } from '@preact/signals-core';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onPhase, Phase } from '../../../loader/decorators/on-phase.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { logger } from '../../../loader/essentials/logger.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';

const log = logger.logGroup({ prefix: 'MetaKeywordsCollector' });

/**
 * Normalizes a unicode string and replace special characters to their ASCII equivalent.
 *
 * @param str the string to normalize
 * @returns the normalized string
 */
const normalize = (str: string): string => str
    // convert the string to lowercase
    .toLowerCase()
    // replace german umlaut characters with their non-umlaut equivalents using character encoding
    .replace(/\u00fc/g, 'ue').replace(/\u00e4/g, 'ae').replace(/\u00f6/g, 'oe').replace(/\u00df/g, 'ss')
    // remove diacritics (accents) using Unicode normalization (NFD) and regex
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * A TypeGuard to check if a given Node is a HTMLMetaElement.
 *
 * @param node the node to check
 * @returns true if the given node is a HTMLMetaElement
 */
const isMetaKeywordsElement = (node: Node): node is HTMLMetaElement => node.nodeName === 'META' && (node as HTMLMetaElement).name === 'keywords';

/**
 * Creates a MutationObserver to monitor changes to meta keywords.
 *
 * @param metaKeywordsChanged a callback function to call when meta keywords changed
 * @returns the MutationObserver
 */
const createMetaKeywordsMutationObserver = (metaKeywordsChanged: () => void): void => {
    // create a MutationObserver to monitor changes to meta keywords
    const observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                // check if meta keywords node is added or removed
                const addedMetaKeywords = Array.from(mutation.addedNodes).filter(isMetaKeywordsElement);
                const removedMetaKeywords = Array.from(mutation.removedNodes).filter(isMetaKeywordsElement);
                if (addedMetaKeywords.length || removedMetaKeywords.length) {
                    return metaKeywordsChanged();
                }
            } else if (mutation.type === 'attributes') {
                // check if content attribute of meta keywords node is modified
                if (isMetaKeywordsElement(mutation.target) && mutation.attributeName === 'content') {
                    return metaKeywordsChanged();
                }
            }
        }
    });
    // configure observer and start observing the target node
    observer.observe(document.head, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['content'],
    });
}

@injectionTarget()
export class MetaKeywordsCollector {

    @inject() private configResolver!: ConfigResolver;
    @inject() private globalTargeting!: GlobalTargeting;

    /** A signal that emits the current meta keywords. */
    #metaKeywords$$ = signal<string[]>([]);

    constructor() {
        // the effect is used to collect the current meta keywords to globalTargeting
        effect(() => {
            // subscribe to the signal which runs the effect again when the meta keywords change
            const keywords = this.#metaKeywords$$.value.slice(0, 6);
            // if there are no keywords, we do not need to add or remove them
            if (!keywords.length) return;

            // adding keywords to globalTargeting
            log.debug('add keywords %o', [keywords]);
            this.globalTargeting.addValues('keywords', keywords);

            // cleanup function to remove previos keywords
            return () => {
                log.debug('remove keywords %o', [keywords]);
                this.globalTargeting.deleteValues('keywords', keywords);
            }
        });
    }

    /**
     * reading meta keywords from the DOM and write it to the signal
     */
    #update() {
        const elements = Array.from(document.querySelectorAll('meta[name="keywords"]')).filter(isMetaKeywordsElement);
        const keywords = elements.map(element => element.content).filter(isNonEmptyString).join(',');
        this.#metaKeywords$$.value = normalize(keywords).replace(/,\s?/g, '+').split('+').filter(isNonEmptyString).filter(filterDuplicates);
    }

    @onPhase({ phase: Phase.TARGETING })
    public collectKeywords(): void {
        // if its disabled in the config, we do not need to collect the meta keywords
        if (!this.configResolver.getConfig().targeting.common?.collectMetaKeys) {
            log.debug('collectMetaKeys disabled in config');
            return;
        }

        // create a MutationObserver to monitor changes to meta keywords; whenever the meta keywords change, update the keywords
        createMetaKeywordsMutationObserver(() => this.#update());

        // update the keywords initially
        this.#update();
    }
}
